// ============================================================================
// Job Merge Logic (domain)
// ============================================================================
// Pure merge logic, shared across ImportTable and Wizard. UI-specific concerns
// (display names, value formatting) live in the MergeConflictModal component
// folder alongside the modal that consumes them.

import type { Job } from "../types/Job";
import type { MergeConflict, MergeConflictOption, MergeResolutions, MergeJobsOptions } from "../types/jobMerge";
import { Geo } from "@rogoag/geo";
import { combineBoundaryPolygons } from "./jobGeoData";

/**
 * Properties that should be merged when combining jobs.
 * Excludes: id (unique identifier), files (handled separately), and system-generated fields.
 */
const MERGEABLE_PROPERTIES: (keyof Job)[] = [
	"importGrowerName",
	"importFarmName",
	"importFieldName",
	"importDensity",
	"importDepth",
	"externalTestPackageId",
	"externalFrequencyOfMicros",
	"isReadyToSample",
	"isTestJob",
	"fieldPriority",
	"salespersonIds",
	"branchIds",
	"lastCrop",
	"nextCrop",
	"yieldGoal",
	"dateFieldReadyEstimated",
	"labSubmissionCode",
	"labInstructions",
	"growerPhoneNumber",
	"submissionNotes",
	"submitterEmail",
	"submitterName",
	"eventId",
	"dealIds",
	"microsList",
	"collectBoundary",
	"importLabName",
	"externalLabId",
	"externalId",
	"externalFarmId",
];

/**
 * Check if a value is considered "truthy" for merge purposes.
 * Empty strings, 0, false, null, undefined, and empty arrays are falsy.
 */
export function isTruthyForMerge(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (value === "") return false;
	if (value === 0) return false;
	if (value === false) return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
}

/**
 * Check if two values are equal for merge conflict detection.
 */
function areValuesEqual(a: unknown, b: unknown): boolean {
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		return a.every((val, idx) => areValuesEqual(val, b[idx]));
	}

	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime();
	}

	if (typeof a === "object" && typeof b === "object" && a !== null && b !== null) {
		return JSON.stringify(a) === JSON.stringify(b);
	}

	return a === b;
}

/**
 * Create a label for a job in the conflict resolution UI.
 */
function getJobLabel(job: Job, index: number): string {
	const fieldName = job.importFieldName || job.fieldName || `Job ${index + 1}`;
	return fieldName;
}

/**
 * Detect merge conflicts between multiple jobs.
 * Returns an array of conflicts where multiple jobs have different truthy values.
 */
export function detectMergeConflicts(jobs: Job[]): MergeConflict[] {
	const conflicts: MergeConflict[] = [];

	for (const propKey of MERGEABLE_PROPERTIES) {
		const truthyValues: MergeConflictOption[] = [];

		for (let i = 0; i < jobs.length; i++) {
			const job = jobs[i];
			const value = job[propKey];

			if (isTruthyForMerge(value)) {
				const existingOption = truthyValues.find((opt) => areValuesEqual(opt.value, value));
				if (!existingOption) {
					truthyValues.push({
						value,
						jobIndex: i,
						jobLabel: getJobLabel(job, i),
					});
				}
			}
		}

		if (truthyValues.length >= 2) {
			conflicts.push({
				propertyKey: propKey,
				options: truthyValues,
			});
		}
	}

	return conflicts;
}

/**
 * Compute the merged value for a property using truthy-wins logic.
 * If resolutions are provided and this property has a resolution, use that.
 * Otherwise, find the first truthy value among the jobs.
 */
export function getMergedValue(jobs: Job[], propKey: keyof Job, resolutions?: MergeResolutions): unknown {
	if (resolutions && propKey in resolutions) {
		return resolutions[propKey];
	}

	for (const job of jobs) {
		const value = job[propKey];
		if (isTruthyForMerge(value)) {
			return value;
		}
	}

	return jobs[0]?.[propKey];
}

/**
 * Merge GeoJSON feature properties from multiple FeatureCollections using first-truthy-wins.
 * Keeps the base (first valid) collection's geometry unchanged — only merges the `properties`
 * object on each feature from corresponding features in other collections.
 */
export function mergeFeatureProperties(
	featureCollections: (GeoJSON.FeatureCollection | undefined)[],
): GeoJSON.FeatureCollection | undefined {
	const valid = featureCollections.filter((c): c is GeoJSON.FeatureCollection => !!c);
	if (valid.length === 0) return undefined;

	const base = structuredClone(valid[0]);
	for (const fc of valid.slice(1)) {
		for (let i = 0; i < base.features.length && i < fc.features.length; i++) {
			const baseProps = base.features[i].properties || {};
			const otherProps = fc.features[i].properties || {};
			for (const [key, value] of Object.entries(otherProps)) {
				if (!(key in baseProps) || baseProps[key] == null || baseProps[key] === "") {
					if (value != null && value !== "") {
						baseProps[key] = value;
					}
				}
			}
			base.features[i].properties = baseProps;
		}
	}
	return base;
}

/**
 * Merge multiple jobs into a single job.
 * Uses the first job as the base, merges GeoJSON via Geo.merge(),
 * applies truthy-wins logic for all mergeable properties,
 * and merges fileOptions arrays from all source jobs.
 *
 * @param jobs - Jobs to merge (must have at least 2)
 * @param resolutionsOrOptions - Optional conflict resolutions or options object
 * @returns The merged job (uses the first job's ID)
 */
export function mergeJobs(jobs: Job[], resolutionsOrOptions?: MergeResolutions | MergeJobsOptions): Job {
	if (jobs.length < 2) {
		throw new Error("mergeJobs requires at least 2 jobs");
	}

	// Normalize arguments: accept either MergeResolutions or MergeJobsOptions
	const isMergeJobsOptions =
		resolutionsOrOptions &&
		typeof resolutionsOrOptions === "object" &&
		("resolutions" in resolutionsOrOptions || "skipMergeGeoTypes" in resolutionsOrOptions);
	const options: MergeJobsOptions = isMergeJobsOptions
		? (resolutionsOrOptions as MergeJobsOptions)
		: { resolutions: resolutionsOrOptions as MergeResolutions | undefined };

	const { resolutions, skipMergeGeoTypes } = options;
	const skipSet = new Set(skipMergeGeoTypes || []);

	const baseJob = jobs[0];

	const mergeGeoJSON = (collections: (typeof baseJob.files.boundaryGeoJSON)[]) => {
		const valid = collections.filter((c): c is NonNullable<typeof c> => !!c);
		return valid.length > 0 ? Geo.merge(valid) : undefined;
	};

	// Boundaries are dissolved to a SINGLE feature after the merge: Geo.merge
	// concatenates one feature per source job, but every downstream consumer
	// (map projection, scheduling point-in-polygon) reads only features[0], so a
	// multi-feature boundary would under-draw once submitted. Collapsing to one
	// MultiPolygon feature keeps that single-feature assumption valid everywhere.
	const dissolveBoundary = (collection: typeof baseJob.files.boundaryGeoJSON) => {
		if (!collection?.features?.length) return collection;
		const combined = combineBoundaryPolygons(collection.features);
		if (!combined) return collection;
		return {
			...collection,
			features: [
				{
					type: "Feature" as const,
					geometry: combined,
					properties: collection.features[0].properties ?? {},
				},
			],
		};
	};

	const mergedJob: Job = {
		...baseJob,
		files: {
			...baseJob.files,
			boundaryGeoJSON: skipSet.has("boundary")
				? mergeFeatureProperties(jobs.map((j) => j.files?.boundaryGeoJSON))
				: dissolveBoundary(mergeGeoJSON(jobs.map((j) => j.files?.boundaryGeoJSON))),
			pointsGeoJSON: skipSet.has("points")
				? mergeFeatureProperties(jobs.map((j) => j.files?.pointsGeoJSON))
				: mergeGeoJSON(jobs.map((j) => j.files?.pointsGeoJSON)),
			zonesGeoJSON: skipSet.has("zones")
				? mergeFeatureProperties(jobs.map((j) => j.files?.zonesGeoJSON))
				: mergeGeoJSON(jobs.map((j) => j.files?.zonesGeoJSON)),
		},
	};

	for (const propKey of MERGEABLE_PROPERTIES) {
		const mergedValue = getMergedValue(jobs, propKey, resolutions);
		(mergedJob as any)[propKey] = mergedValue;
	}

	return mergedJob;
}
